import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import {
  Body1,
  Caption1,
  Caption1Strong,
  Caption1Stronger,
  Link as FluentLink,
  Tooltip,
} from '@fluentui/react-components';
import {
  AddCircleRegular,
  ArchiveRegular,
  ArrowRight12Filled,
  CalendarLtrRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  EditRegular,
  FlagRegular,
  LinkRegular,
  PersonRegular,
  RenameRegular,
  TextDescriptionRegular,
  type FluentIcon,
} from '@fluentui/react-icons';
import AvatarGroup from '@modules/users/components/AvatarGroup';
import UserLabel from '@modules/users/components/UserLabel';
import { AgoDateLabel } from '@shared/components/dates/AgoDateLabel';
import { DateLabel } from '@shared/components/dates/DateLabel';
import { TERMINOLOGY } from '@shared/i18n';
import { TASK_COLUMN_LABELS, TASK_PRIORITY_LABELS, TASK_STATUS_METADATA } from '../../tasks/constants';
import { getTaskPath } from '../../tasks/paths';
import {
  ETaskHistoryType,
  type ETaskStatus,
  type TTaskActivity,
  type TTaskActivityChanges,
  type TTaskChange,
} from '../../tasks/types';
import { RECENT_ACTIVITIES_MORE_LABEL } from '../constants';
import { diffWordSegments } from '../helpers/diffWordSegments';

const FIELD_ICONS: Record<Exclude<keyof TTaskActivityChanges, 'status'>, FluentIcon> = {
  title: RenameRegular,
  linkedItems: LinkRegular,
  description: TextDescriptionRegular,
  assignee: PersonRegular,
  dueDate: CalendarLtrRegular,
  priority: FlagRegular,
};

const getActivityIcon = (activity: TTaskActivity): FluentIcon => {
  switch (activity.type) {
    case ETaskHistoryType.CREATED:
      return AddCircleRegular;
    case ETaskHistoryType.ARCHIVED:
      return ArchiveRegular;
    case ETaskHistoryType.UPDATED: {
      const fieldIcons = Object.entries(FIELD_ICONS)
        .filter(([field]) => field in activity.changes)
        .map(([, icon]) => icon);
      return fieldIcons.length === 1 ? fieldIcons[0] : EditRegular;
    }
  }
};

const ActivityIcon = ({ activity }: { activity: TTaskActivity }) => {
  if (activity.type === ETaskHistoryType.UPDATED && activity.changes.status) {
    const { icon: Icon, color } = TASK_STATUS_METADATA[activity.changes.status.after];
    return <Icon fontSize={18} style={{ color }} />;
  }
  const Icon = getActivityIcon(activity);
  return <Icon fontSize={18} className="text-neutralFg-3" />;
};

const StatusChange = ({ before, after }: TTaskChange<ETaskStatus>) => (
  <span className="inline-flex items-center gap-1">
    <Caption1Strong style={{ color: TASK_STATUS_METADATA[before].color }}>
      {TASK_STATUS_METADATA[before].label}
    </Caption1Strong>
    <ArrowRight12Filled />
    <Caption1Stronger style={{ color: TASK_STATUS_METADATA[after].color }}>
      {TASK_STATUS_METADATA[after].label}
    </Caption1Stronger>
  </span>
);

// Inline, not inline-flex, so a long value wraps instead of overflowing.
const FieldChange = ({ label, before, after }: { label: string } & TTaskChange<ReactNode>) => (
  <span>
    {label} <Caption1Strong>{before}</Caption1Strong>
    <ArrowRight12Filled className="mx-1" />
    <Caption1Stronger className="text-neutralFg-1">{after}</Caption1Stronger>
  </span>
);

const DiffText = ({
  before = '',
  after = '',
  insertClassName,
}: Partial<TTaskChange<string>> & { insertClassName?: string }) => {
  const segments = useMemo(() => diffWordSegments(before, after), [before, after]);
  return (
    <>
      {segments.map(({ text, change }, index) => {
        if (change === 'delete')
          return (
            <del key={index} className="mr-0.5 rounded-sm bg-red-600/15 text-red-600">
              {text}
            </del>
          );
        if (change === 'insert')
          return (
            <ins key={index} className={classNames('rounded-sm bg-green-500/25 no-underline', insertClassName)}>
              {text}
            </ins>
          );
        return <span key={index}>{text}</span>;
      })}
    </>
  );
};

const LinkedItemsChange = ({ added, removed }: NonNullable<TTaskActivityChanges['linkedItems']>) => (
  <span>
    Linked items:{' '}
    <Caption1Stronger className="text-neutralFg-1">
      {[added.length && `${added.length} added`, removed.length && `${removed.length} removed`]
        .filter(Boolean)
        .join(', ')}
    </Caption1Stronger>
  </span>
);

const TextChange = ({ label, ...change }: { label: string } & Partial<TTaskChange<string>>) => (
  <span className="break-words">
    {label}{' '}
    <Caption1Strong>
      <DiffText {...change} insertClassName="font-bold text-neutralFg-1" />
    </Caption1Strong>
  </span>
);

const getDescriptionVerb = ({ before, after }: Partial<TTaskChange<string>>) => {
  if (!before) return 'added';
  if (!after) return 'removed';
  return 'changed';
};

type TDiffToggle = { open: boolean; onToggle: () => void };

const DescriptionChange = ({ verb, open, onToggle }: { verb: string } & TDiffToggle) => {
  const Chevron = open ? ChevronUpRegular : ChevronDownRegular;
  return (
    <span>
      Description {verb}{' '}
      <FluentLink
        // The row is a link, so a click here must neither open the task nor close the dialog.
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onToggle();
        }}
        onAuxClick={(event) => event.preventDefault()}
      >
        <Caption1>{open ? 'Hide changes' : 'Show changes'}</Caption1> <Chevron fontSize={12} className="align-middle" />
      </FluentLink>
    </span>
  );
};

const DescriptionDiff = (change: Partial<TTaskChange<string>>) => {
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const mark = container.current?.querySelector('ins, del');
    if (container.current && mark instanceof HTMLElement)
      container.current.scrollTop = Math.max(0, mark.offsetTop - 24);
    container.current?.scrollIntoView({ block: 'nearest' });
  }, []);
  return (
    <div
      ref={container}
      className="relative mb-1 ml-[52px] mr-2 mt-1 max-h-60 scroll-mb-2 overflow-y-auto break-words rounded-md border border-neutralStroke-2 p-2"
    >
      <Body1 className="!whitespace-pre-wrap">
        <DiffText {...change} />
      </Body1>
    </div>
  );
};

const SHOW_MORE_HINT = `Click "${RECENT_ACTIVITIES_MORE_LABEL}" to see the details.`;

type TChange = { name: string; text: ReactNode; brief?: ReactNode };

const formatAssignee = (id?: string) => (id ? <UserLabel id={id} /> : 'Unassigned');
const formatDueDate = (date?: string) =>
  date ? (
    <DateLabel short timeZone="UTC">
      {date}
    </DateLabel>
  ) : (
    'No due date'
  );

const getChanges = (
  { status, title, linkedItems, description, assignee, dueDate, priority }: TTaskActivityChanges,
  toggle: TDiffToggle
): TChange[] => {
  const field = (name: string, before: ReactNode, after: ReactNode) => ({
    name,
    text: <FieldChange label={name} before={before} after={after} />,
  });
  return [
    status && { name: TASK_COLUMN_LABELS.status, text: <StatusChange {...status} /> },
    title && { name: 'Title', text: <TextChange label="Title" {...title} /> },
    linkedItems && { name: 'Linked items', text: <LinkedItemsChange {...linkedItems} /> },
    description && {
      name: 'Description',
      text: <DescriptionChange verb={getDescriptionVerb(description)} {...toggle} />,
      brief: `Description ${getDescriptionVerb(description)}`,
    },
    assignee && field(TASK_COLUMN_LABELS.assignee, formatAssignee(assignee.before), formatAssignee(assignee.after)),
    dueDate && field(TASK_COLUMN_LABELS.dueDate, formatDueDate(dueDate.before), formatDueDate(dueDate.after)),
    priority &&
      field(TASK_COLUMN_LABELS.priority, TASK_PRIORITY_LABELS[priority.before], TASK_PRIORITY_LABELS[priority.after]),
  ].filter((change) => change !== undefined);
};

const formatNames = (names: string[]) => {
  // A name of two words stays on one line when the sentence wraps.
  const words = names.map((name, index) => (index === 0 ? name : name.toLowerCase()).replace(' ', '\u00A0'));
  return words.length === 1 ? words[0] : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
};

const ChangeList = ({ className, lines }: { className?: string; lines: ReactNode[] }) =>
  lines.length > 1 ? (
    <ul className={classNames('list-disc space-y-0.5 pl-2.5', className)}>
      {lines.map((line, index) => (
        <li key={index}>{line}</li>
      ))}
    </ul>
  ) : (
    <div className={className}>{lines}</div>
  );

const ChangesSummary = ({ changes }: { changes: TChange[] }) => (
  <Tooltip
    withArrow
    relationship="description"
    content={
      <div>
        <ChangeList
          className="text-neutralFg-3"
          lines={changes.map(({ name, text, brief }) => (
            <span key={name}>{brief ?? text}</span>
          ))}
        />
        {changes.some(({ brief }) => brief) && <div className="mt-1 text-neutralFg-3">{SHOW_MORE_HINT}</div>}
      </div>
    }
  >
    <span>{formatNames(changes.map(({ name }) => name))} changed</span>
  </Tooltip>
);

const getActivityTexts = (activity: TTaskActivity, multiline: boolean, toggle: TDiffToggle): ReactNode[] => {
  switch (activity.type) {
    case ETaskHistoryType.CREATED:
      return [`${TERMINOLOGY.task.upper} created`];
    case ETaskHistoryType.ARCHIVED:
      return [`${TERMINOLOGY.task.upper} archived`];
    case ETaskHistoryType.UPDATED: {
      const changes = getChanges(activity.changes, toggle);
      if (multiline) return changes.map(({ text }) => text);
      // A title or several changes would crowd the card, so their values go in a tooltip.
      if (changes.length > 1 || activity.changes.title) return [<ChangesSummary changes={changes} />];
      return changes.map(({ brief, text }) =>
        brief ? (
          <Tooltip withArrow relationship="description" content={SHOW_MORE_HINT}>
            <span>{brief}</span>
          </Tooltip>
        ) : (
          text
        )
      );
    }
  }
};

const ActivityChanges = ({
  activity,
  multiline,
  toggle,
}: {
  activity: TTaskActivity;
  multiline: boolean;
  toggle: TDiffToggle;
}) => (
  <ChangeList
    className="text-balance text-xs text-neutralFg-3"
    lines={getActivityTexts(activity, multiline, toggle).map((text, index) => (
      <Caption1 key={index}>{text}</Caption1>
    ))}
  />
);

type TProps = {
  projectId: string;
  activity: TTaskActivity;
  multiline?: boolean;
  onClick?: () => void;
};

const TaskActivityRow = ({ projectId, activity, multiline = false, onClick }: TProps) => {
  const { search } = useLocation();
  const [isDiffOpen, setIsDiffOpen] = useState(false);
  const className = 'flex items-center gap-3 rounded-md px-2 py-1.5';
  const content = (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutralBg-4 group-hover:bg-neutralBg-5">
        <ActivityIcon activity={activity} />
      </span>
      <div className="flex min-w-0 grow flex-col">
        <Body1 wrap className={classNames('break-words', !multiline && '!line-clamp-2')}>
          {activity.task.name}
        </Body1>
        <ActivityChanges
          activity={activity}
          multiline={multiline}
          toggle={{ open: isDiffOpen, onToggle: () => setIsDiffOpen(!isDiffOpen) }}
        />
      </div>
      <span className="flex shrink-0 items-center gap-2">
        <Tooltip
          withArrow
          relationship="description"
          content={
            <DateLabel withTime short>
              {activity.createdDate}
            </DateLabel>
          }
        >
          <Caption1 className="text-neutralFg-3">
            <AgoDateLabel short>{activity.createdDate}</AgoDateLabel>
          </Caption1>
        </Tooltip>
        <AvatarGroup ids={[activity.createdByUser]} size={24} />
      </span>
    </>
  );
  const description = activity.type === ETaskHistoryType.UPDATED ? activity.changes.description : undefined;
  return (
    <>
      {activity.task.archived ? (
        <div className={className}>{content}</div>
      ) : (
        <Link
          to={getTaskPath(projectId, activity.task._id, search)}
          onClick={onClick}
          className={classNames(className, 'group hover:bg-neutralBg-3')}
        >
          {content}
        </Link>
      )}
      {isDiffOpen && description && <DescriptionDiff {...description} />}
    </>
  );
};
export default TaskActivityRow;
